import { Banner, Button } from "@salesko/design-system";
import { Link } from "@tanstack/react-router";
import { Bot, Coins, CreditCard, Database, LogOut, Monitor, Sparkles, UserRound, Wand2 } from "lucide-react";
import { useMemo } from "react";
import { GoogleGlyph } from "../../components/GoogleGlyph";
import { clearStoredSaleskoSession } from "../../lib/api";
import { LanguageSwitcher, useI18n, type Dictionary } from "../../i18n";
import {
  DataExportSettingsPanel,
  GenericAgentSettingsPanel,
  WorkspaceSettingsPanel
} from "../components/TenantAdminPanel";
import { useAgentSession } from "../hooks/useAgentSession";
import { BillingPanel } from "./BillingPanel";
import { ComputersPanel } from "./ComputersPanel";
import { CreditsPanel } from "./CreditsPanel";
import { DataToolsPanel } from "./DataToolsPanel";
import { PrivateAgentPanel } from "./PrivateAgentPanel";

export const settingsSectionDefs = [
  { icon: UserRound, id: "account", slug: "account" },
  { icon: CreditCard, id: "billing", slug: "billing" },
  { icon: Coins, id: "credits", slug: "credits" },
  { icon: Monitor, id: "computers", slug: "computers" },
  { icon: Bot, id: "privateAgent", slug: "private-agent" },
  { icon: Wand2, id: "agent", slug: "agent" },
  { icon: Sparkles, id: "data", slug: "data" },
  { icon: Database, id: "advanced", slug: "advanced" }
] as const;

export type SettingsSectionId = (typeof settingsSectionDefs)[number]["id"];
export type SettingsSectionSlug = (typeof settingsSectionDefs)[number]["slug"];

export function settingsSectionFromSlug(slug: string): SettingsSectionId | null {
  return settingsSectionDefs.find((section) => section.slug === slug)?.id ?? null;
}

export function settingsSectionPath(sectionId: SettingsSectionId) {
  const slug = settingsSectionDefs.find((section) => section.id === sectionId)?.slug;
  if (!slug) {
    throw new Error(`Unknown settings section: ${sectionId}`);
  }
  return `/settings/${slug}`;
}

/** Maps the post-checkout query to a confirmation message on Billing only. */
export function billingStatusFromSearch(search: string): { tone: "success" | "neutral"; text: string } | null {
  const billing = new URLSearchParams(search).get("billing");
  if (billing === "success") {
    return { tone: "success", text: "Plan activated. Deep Research is now available; your Agent credits were added." };
  }
  if (billing === "cancel") {
    return { tone: "neutral", text: "Checkout canceled. Your plan hasn't changed." };
  }
  return null;
}

function BillingStatusBanner() {
  const { t } = useI18n();
  const status = useMemo(
    () => (typeof window === "undefined" ? null : billingStatusFromSearch(window.location.search)),
    []
  );
  if (!status) {
    return null;
  }
  return (
    <Banner status={status.tone === "success" ? "success" : "neutral"} style={{ maxWidth: 760 }}>
      {status.tone === "success" ? t.settings.billingSuccess : t.settings.billingCancel}
    </Banner>
  );
}

/**
 * Dedicated Settings shell. The active sub-route mounts exactly one panel, so
 * direct links, refresh, back/forward, data loading, and focus all share the
 * same page boundary instead of scrolling through a mixed document.
 */
export function SettingsSurface({
  activeFrameRecordId,
  activeSection
}: {
  activeFrameRecordId: string | null;
  activeSection: SettingsSectionId;
}) {
  const { t } = useI18n();
  const { availableAuthProviders, handleAuthProviderSignIn, storedSession } = useAgentSession();
  const hasSession = Boolean(storedSession);
  const settingsSections = useMemo(
    () =>
      settingsSectionDefs.map((item) => ({
        ...item,
        ...settingsSectionCopy(t, item.id)
      })),
    [t]
  );
  const activeCopy = settingsSections.find((item) => item.id === activeSection) ?? settingsSections[0]!;

  return (
    <main className="ops-console settings-surface">
      <aside className="ops-sidebar" aria-label={t.settings.navigationAriaLabel}>
        <a className="ops-brand" href="/app">
          <span className="brand-mark" aria-hidden="true">
            <img alt="" src="/icons/logo-mark.svg" />
          </span>
          <span className="brand-copy">
            <strong>Salesko</strong>
            <span>{t.settings.title}</span>
          </span>
        </a>

        <nav className="ops-nav" aria-label={t.settings.sectionsAriaLabel}>
          {settingsSections.map((item) => {
            const Icon = item.icon;
            const active = item.id === activeSection;
            return (
              <Link
                aria-current={active ? "page" : undefined}
                className={`ops-nav-item${active ? " is-active" : ""}`}
                key={item.slug}
                params={{ section: item.slug }}
                search={activeFrameRecordId ? { frame: activeFrameRecordId } : {}}
                to="/settings/$section"
              >
                <Icon size={17} />
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </span>
              </Link>
            );
          })}
        </nav>

        <div className="ops-sidebar-footer">
          <a className="ops-system-link" href="/app">
            {t.nav.workspace}
          </a>
          <a className="ops-system-link" href="/connectors">
            {t.nav.connectors}
          </a>
        </div>
      </aside>

      <section className="ops-main" aria-labelledby="settings-title">
        <header className="ops-header">
          <div>
            <p className="eyebrow">{t.settings.title}</p>
            <h1 id="settings-title">{activeCopy.label}</h1>
            <p>{activeCopy.description}</p>
          </div>
          <div className="settings-header-actions">
            <LanguageSwitcher className="login-lang" />
            {hasSession ? (
              <Button
                iconLeft={<LogOut size={15} />}
                onClick={() => clearStoredSaleskoSession()}
                size="sm"
                variant="secondary"
              >
                {t.settings.signOut}
              </Button>
            ) : (
              availableAuthProviders.map((provider) => (
                <Button
                  iconLeft={provider === "google" ? <GoogleGlyph size={15} /> : <UserRound size={15} />}
                  key={provider}
                  onClick={() => handleAuthProviderSignIn(provider)}
                  size="sm"
                  variant="secondary"
                >
                  {t.common.signInWith(provider)}
                </Button>
              ))
            )}
          </div>
        </header>

        {activeSection === "billing" ? <BillingStatusBanner /> : null}
        {renderSettingsSection(activeSection, {
          activeFrameRecordId,
          hasSession,
          storedSession
        })}
      </section>
    </main>
  );
}

function renderSettingsSection(
  activeSection: SettingsSectionId,
  context: {
    activeFrameRecordId: string | null;
    hasSession: boolean;
    storedSession: ReturnType<typeof useAgentSession>["storedSession"];
  }
) {
  switch (activeSection) {
    case "account":
      return <WorkspaceSettingsPanel enabled={context.hasSession} session={context.storedSession} />;
    case "billing":
      return <BillingPanel enabled={context.hasSession} />;
    case "credits":
      return <CreditsPanel enabled={context.hasSession} />;
    case "computers":
      return <ComputersPanel enabled={context.hasSession} />;
    case "privateAgent":
      return <PrivateAgentPanel enabled={context.hasSession} />;
    case "agent":
      return <GenericAgentSettingsPanel enabled={context.hasSession} session={context.storedSession} />;
    case "data":
      return <DataExportSettingsPanel enabled={context.hasSession} />;
    case "advanced":
      return <DataToolsPanel activeFrameRecordId={context.activeFrameRecordId} enabled={context.hasSession} />;
  }
}

function settingsSectionCopy(t: Dictionary, id: SettingsSectionId) {
  switch (id) {
    case "account":
      return { description: t.settings.sidebar.accountDescription, label: t.settings.sidebar.account };
    case "billing":
      return { description: t.settings.sidebar.billingDescription, label: t.settings.sidebar.billing };
    case "credits":
      return { description: t.settings.sidebar.creditsDescription, label: t.settings.sidebar.credits };
    case "computers":
      return { description: t.settings.sidebar.computersDescription, label: t.settings.sidebar.computers };
    case "privateAgent":
      return { description: t.settings.sidebar.privateAgentDescription, label: t.settings.sidebar.privateAgent };
    case "agent":
      return { description: t.settings.sidebar.agentDescription, label: t.settings.sidebar.agent };
    case "data":
      return { description: t.settings.sidebar.dataDescription, label: t.settings.sidebar.data };
    case "advanced":
      return { description: t.settings.sidebar.advancedDescription, label: t.settings.sidebar.advanced };
  }
}
